import React from 'react';
import { GetServerSideProps } from 'next';
import { Profile, ProfileController, send, withCookie } from '@oward/openapi';
import { getLocaleProps, LocaleProps, WithLocale } from '@components/I18n';
import { WithAuthentication, getAuthenticationProps, AuthenticationProps } from '@components/Authentication';
import { ProfilePersoPage, PageWrapper } from '@components/.';
import { ProfilePersoPageNotFound } from '@components/Pages/Profile/ProfilePersoPageNotFound';
import { useTranslate } from '@hooks/use-translate';
import { findTranslationOrDefault } from '@oward/common-utils';
import { ERR_MSG_UNAUTHORIZED, ProfileDescription } from '@utils/.';

interface PageProps {
  profile?: Profile;
  errorMsgKey?: string;
}

export const getServerSideProps: GetServerSideProps = async context => {
  let pageProps: PageProps;
  const locale = context.params.lang as string;
  const localeProps = await getLocaleProps(locale);
  const authenticationProps = await getAuthenticationProps(context);

  // Authentication is needed when getting perso page, cause admin and user can access non-visible perso page
  let cookie = context.req.headers.cookie;

  try {
    let profile = cookie === undefined ?
      await send(ProfileController.findOneForPage(context.params.profilePath as string, locale))
      : await withCookie(cookie)(ProfileController.findOneForPage(context.params.profilePath as string, locale));

    pageProps = {
      profile: profile,
    }
  }
  catch (error) {
    pageProps = {
      errorMsgKey: error.message === ERR_MSG_UNAUTHORIZED ? 'page.profile.error_profile_unauthorized' : 'page.profile.error_profile_not_found',
    }
  }

  return {
    props: {
      ...pageProps,
      ...authenticationProps,
      ...localeProps
    }
  }
}

const Page: React.FC<PageProps> = props => {
  const { locale } = useTranslate();
  return (
    <PageWrapper
      pageTitle={!props.errorMsgKey &&
        `${props.profile?.name} (${findTranslationOrDefault(props.profile?.job, locale)}) | ${findTranslationOrDefault(props.profile.location.city, locale)}`
      }
      pageDescription={!props.errorMsgKey && ProfileDescription(props.profile)}
      pageImage={!props.errorMsgKey && (props.profile?.avatarUrl ?? undefined)}
      noIndex={!!props.errorMsgKey}
    >
      {props.errorMsgKey ?
        <ProfilePersoPageNotFound errorMsgKey={props.errorMsgKey} />
        :
        <ProfilePersoPage profile={props.profile} />
      }
    </PageWrapper>
  );
}

const CompletePage: React.FC<PageProps & LocaleProps & AuthenticationProps> = props => {
  return (
    <WithLocale {...props}>
      <WithAuthentication {...props}>
        <Page {...props} />
      </WithAuthentication>
    </WithLocale>
  );
}

export default CompletePage;




